// ============================================
// Authentication Context
// ============================================

import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithPopup,
  signInAnonymously,
  signInWithCustomToken,
  GoogleAuthProvider,
  signOut as firebaseSignOut,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, functions } from '../config/firebase';
import { User, UserRole, CustomClaims } from '../types';

interface ImpersonationInfo {
  targetUid: string;
  targetEmail: string | null;
  targetDisplayName: string | null;
  originalUid: string;
}

interface AuthContextType {
  user: FirebaseUser | null;
  userData: User | null;
  role: UserRole | null;
  roles: UserRole[];
  activeRole: UserRole | null;
  hasMultipleRoles: boolean;
  isAnonymous: boolean;
  loading: boolean;
  error: string | null;
  impersonation: ImpersonationInfo | null;
  signInWithGoogle: () => Promise<void>;
  signInAsPlayer: () => Promise<void>;
  signOut: () => Promise<void>;
  switchRole: (newRole: UserRole) => Promise<void>;
  impersonateUser: (targetUid: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const googleProvider = new GoogleAuthProvider();

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [role, setRole] = useState<UserRole | null>(null);
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [activeRole, setActiveRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(null);


  useEffect(() => {
    console.log('Setting up auth state listener...');
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('Auth state changed:', firebaseUser ? firebaseUser.email : 'no user');
      setUser(firebaseUser);
      setError(null);

      if (firebaseUser) {
        try {
          // Get custom claims for role
          const tokenResult = await firebaseUser.getIdTokenResult();
          const claims = tokenResult.claims as any; // Extended claims with roles array

          // Support both legacy single role and new roles array
          const userRoles: UserRole[] = claims.roles || (claims.role ? [claims.role] : []);
          const userActiveRole: UserRole | null = claims.activeRole || claims.role || null;

          setRole(claims.role || null);
          setRoles(userRoles);
          setActiveRole(userActiveRole);

          console.log('User roles:', userRoles);
          console.log('Active role:', userActiveRole);

          // Get user data from Firestore (only for non-anonymous users)
          if (!firebaseUser.isAnonymous) {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            if (userDoc.exists()) {
              setUserData(userDoc.data() as User);
            } else {
              // Create user document on first sign-in
              const newUser: Partial<User> = {
                uid: firebaseUser.uid,
                displayName: firebaseUser.displayName || 'User',
                email: firebaseUser.email || '',
                photoUrl: firebaseUser.photoURL || undefined,
                createdAt: serverTimestamp() as any,
                lastLoginAt: serverTimestamp() as any,
              };
              await setDoc(doc(db, 'users', firebaseUser.uid), newUser);
              setUserData(newUser as User);
            }
          }
        } catch (err) {
          console.error('Error loading user data:', err);
          setError('Failed to load user data');
        }
      } else {
        setUserData(null);
        setRole(null);
        setRoles([]);
        setActiveRole(null);
      }

      console.log('Setting loading to false');
      setLoading(false);
    }, (error) => {
      // Error callback for onAuthStateChanged
      console.error('Auth state error:', error);
      setLoading(false);
      setError(error.message);
    });

    // Fallback timeout - if auth doesn't respond in 10 seconds, stop loading
    const timeout = setTimeout(() => {
      console.log('Auth timeout - forcing loading to false');
      setLoading(false);
    }, 10000);

    return () => {
      unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const signInWithGoogle = async () => {
    setError(null);
    console.log('Starting Google sign-in popup...');
    try {
      const result = await signInWithPopup(auth, googleProvider);
      console.log('Sign-in successful:', result.user?.email);
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      console.error('Error code:', err.code);
      // Handle popup closed by user
      if (err.code === 'auth/popup-closed-by-user') {
        console.log('User closed the popup');
        return;
      }
      setError(err.message || 'Failed to sign in with Google');
      throw err;
    }
  };

  const signInAsPlayer = async () => {
    setError(null);
    try {
      await signInAnonymously(auth);
    } catch (err: any) {
      console.error('Anonymous sign-in error:', err);
      setError(err.message || 'Failed to sign in anonymously');
      throw err;
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await firebaseSignOut(auth);
    } catch (err: any) {
      console.error('Sign-out error:', err);
      setError(err.message || 'Failed to sign out');
      throw err;
    }
  };

  const switchRole = async (newRole: UserRole) => {
    if (!user) {
      throw new Error('Must be authenticated to switch roles');
    }

    if (!roles.includes(newRole)) {
      throw new Error(`You do not have the ${newRole} role`);
    }

    setError(null);
    try {
      const switchActiveRole = httpsCallable(functions, 'switchActiveRole');
      await switchActiveRole({ newRole });

      // Force token refresh to get updated claims
      await user.getIdToken(true);

      // Update local state
      setActiveRole(newRole);

      console.log('Switched to role:', newRole);
    } catch (err: any) {
      console.error('Role switch error:', err);
      setError(err.message || 'Failed to switch role');
      throw err;
    }
  };

  const impersonateUser = async (targetUid: string) => {
    if (!user) {
      throw new Error('Must be authenticated to impersonate');
    }

    // Only superadmin can impersonate
    if (role !== 'superadmin') {
      throw new Error('Only superadmin can impersonate users');
    }

    setError(null);
    try {
      // Store original user info before impersonating
      const originalUid = user.uid;

      const createImpersonationToken = httpsCallable(functions, 'createImpersonationToken');
      const result = await createImpersonationToken({ targetUid }) as any;

      if (!result.data.success) {
        throw new Error('Failed to create impersonation token');
      }

      // Store impersonation info in sessionStorage (persists across page reloads within session)
      const impersonationInfo: ImpersonationInfo = {
        targetUid: result.data.targetUser.uid,
        targetEmail: result.data.targetUser.email,
        targetDisplayName: result.data.targetUser.displayName,
        originalUid,
      };
      sessionStorage.setItem('impersonation', JSON.stringify(impersonationInfo));

      // Sign in with the custom token
      await signInWithCustomToken(auth, result.data.token);

      console.log('Now impersonating:', result.data.targetUser.email);
      setImpersonation(impersonationInfo);
    } catch (err: any) {
      console.error('Impersonation error:', err);
      setError(err.message || 'Failed to impersonate user');
      throw err;
    }
  };

  const stopImpersonation = async () => {
    // Clear impersonation state
    sessionStorage.removeItem('impersonation');
    setImpersonation(null);

    // Sign out and redirect to login
    await firebaseSignOut(auth);

    // User will need to sign in again as themselves
    console.log('Stopped impersonation, signed out');
  };

  // Check for existing impersonation on mount
  useEffect(() => {
    const storedImpersonation = sessionStorage.getItem('impersonation');
    if (storedImpersonation) {
      try {
        setImpersonation(JSON.parse(storedImpersonation));
      } catch (e) {
        sessionStorage.removeItem('impersonation');
      }
    }
  }, []);

  const value: AuthContextType = {
    user,
    userData,
    role,
    roles,
    activeRole,
    hasMultipleRoles: roles.length > 1,
    isAnonymous: user?.isAnonymous ?? false,
    loading,
    error,
    impersonation,
    signInWithGoogle,
    signInAsPlayer,
    signOut,
    switchRole,
    impersonateUser,
    stopImpersonation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
